import { ShieldAlert } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useToolPreconditions } from '@/hooks/use-tool-preconditions';
import { cn } from '@/lib/utils';
import { AccessIcon } from '@/modules/back-testing/datasets/tools/tool-access';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import type { CustomToolStep } from '@/types/custom-tool.types';
import { useCustomToolBuilder } from '../builder/builder-context';
import { isWriteTool } from '../use-step-tools';

const OUTCOME = {
	success: { label: 'Ran', className: 'border-green-200 bg-green-50 text-green-800' },
	// A tool server not running in production mode answers a write with sample data instead of sending it.
	simulated: { label: 'Not sent', className: 'border-amber-200 bg-amber-50 text-amber-800' },
	error: { label: 'Failed', className: 'border-red-200 bg-red-50 text-red-700' },
	skipped: { label: 'Skipped', className: 'border-gray-300 bg-gray-50 text-gray-800' }
} as const;

/** How the step ended in the last test run of the draft. */
const OutcomeBadge = ({ stepId }: { stepId: string }) => {
	const { testing } = useCustomToolBuilder();
	const outcome = testing.outcomes?.[stepId];
	if (!outcome) return null;
	const { label, className } = OUTCOME[outcome.status];
	return (
		<span
			title={outcome.status === 'simulated' ? 'A write, answered with sample data: this tool server is not in production mode' : outcome.message}
			className={cn('shrink-0 rounded-full border px-1.5 text-[10.5px] leading-4', className)}
		>
			{label}
		</span>
	);
};

/** A tool's preconditions are checked by its own name, so not while it runs as a step. */
const PreconditionsIcon = ({ step, className }: { step: CustomToolStep; className?: string }) => {
	const { data } = useToolPreconditions(step.provider, step.tool);
	const count = (data ?? []).filter((rule) => rule.enabled).length;
	if (!count) return null;
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span tabIndex={0} aria-label={`${count} ${count === 1 ? 'precondition' : 'preconditions'} not checked`} className={cn('shrink-0 text-amber-600', className)}>
					<ShieldAlert className='size-3.5' />
				</span>
			</TooltipTrigger>
			<TooltipContent className='max-w-60 text-xs'>
				{`This tool has ${count} ${count === 1 ? 'precondition' : 'preconditions'}. They are not checked when it runs inside a custom tool.`}
			</TooltipContent>
		</Tooltip>
	);
};

/** How the step ended in the last test, its unchecked preconditions, and whether its tool reads or writes. */
export const StepBadges = ({ step, tool, className }: { step: CustomToolStep; tool: ToolDefinitionAction | undefined; className?: string }) => (
	<>
		<OutcomeBadge stepId={step.id} />
		<PreconditionsIcon step={step} className={className} />
		{tool && <AccessIcon riskLevel={isWriteTool(tool) ? 'high' : 'low'} />}
	</>
);
